import { useState, useEffect, useRef } from "react";
import { categories } from "@/mocks/categories";
import { useNavigate } from "react-router-dom";

const articleLinks = [
  { slug: "pandabuyspreadsheet-guide", label: "Complete Guide" },
  { slug: "pandabuyspreadsheet-sneakers", label: "Sneakers Guide" },
  { slug: "pandabuyspreadsheet-hoodies", label: "Hoodies Guide" },
  { slug: "pandabuyspreadsheet-budget", label: "Budget Finds" },
  { slug: "pandabuyspreadsheet-tiktok", label: "TikTok Trends" },
  { slug: "pandabuyspreadsheet-jackets", label: "Jackets Guide" },
  { slug: "pandabuyspreadsheet-accessories", label: "Accessories Guide" },
  { slug: "pandabuyspreadsheet-sets", label: "Sets Guide" },
  { slug: "pandabuyspreadsheet-jersey", label: "Jersey Guide" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuideDropdownOpen, setIsGuideDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setIsGuideDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const textColor = isScrolled ? "text-foreground-700" : "text-background-50";
  const hoverColor = isScrolled ? "hover:text-foreground-900 hover:bg-background-100" : "hover:text-background-100 hover:bg-background-50/15";
  const brandColor = isScrolled ? "text-foreground-900" : "text-background-50";
  const mobileIconColor = isScrolled ? "text-foreground-700" : "text-background-50";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <a
          href="/"
          className="flex items-center gap-2 group cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isScrolled ? "bg-accent-500" : "bg-background-50/25 backdrop-blur-sm"}`}>
            <i className={`text-lg ${isScrolled ? "ri-grid-line text-background-50" : "ri-grid-line text-background-50"}`}></i>
          </div>
          <span className={`font-heading font-semibold text-lg whitespace-nowrap transition-colors ${brandColor}`}>
            pandabuy<span className={`${isScrolled ? "text-accent-500" : "text-accent-300"}`}>.bond</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textColor} ${hoverColor}`}
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </button>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => { setIsDropdownOpen(!isDropdownOpen); setIsGuideDropdownOpen(false); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textColor} ${hoverColor}`}
            >
              <i className="ri-apps-2-line text-base"></i>
              Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-900 transition-colors cursor-pointer"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium whitespace-nowrap">{cat.name}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{cat.description}</span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div ref={guideDropdownRef} className="relative">
            <button
              onClick={() => { setIsGuideDropdownOpen(!isGuideDropdownOpen); setIsDropdownOpen(false); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textColor} ${hoverColor}`}
            >
              <i className="ri-book-open-line text-base"></i>
              Shopping Guide
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isGuideDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {isGuideDropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-56 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg">
                {articleLinks.map((link) => (
                  <button
                    key={link.slug}
                    onClick={() => {
                      setIsGuideDropdownOpen(false);
                      navigate(`/${link.slug}`);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-article-line text-accent-500 text-sm"></i>
                    {link.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Pandabuy Sheet
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
            isScrolled ? "hover:bg-background-100" : "hover:bg-background-50/15"
          }`}
        >
          <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line " + mobileIconColor : "ri-menu-line " + mobileIconColor}`}></i>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <div className="mb-4 flex items-center gap-2">
            <button
              onClick={() => { setIsMobileMenuOpen(false); navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              Home
            </button>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-shopping-bag-3-line"></i>
              Pandabuy Sheet
            </a>
          </div>

          <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-2">Shopping Guides</p>
          <div className="mb-4 grid grid-cols-2 gap-1.5">
            {articleLinks.map((link) => (
              <button
                key={link.slug}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate(`/${link.slug}`);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-article-line text-accent-500"></i>
                {link.label}
              </button>
            ))}
          </div>

          <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 px-2">All Categories</p>
          <div className="mb-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 transition-colors cursor-pointer border-b border-background-100 last:border-0"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                </div>
                <div className="flex flex-col">
                  <span className="font-medium whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400">{cat.description}</span>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300 ml-auto"></i>
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}